'use client';

import { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, FileText } from 'lucide-react';
import { useLanguage } from '@/components/LanguageProvider';
import Link from 'next/link'; 

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CompanyHeader from '@/components/CompanyHeader';
import { IconTelegram, IconDiscord } from '@/components/SocialIcons';

import { showcaseVideos } from './constants';
import { AmbientLight, StickyBottomPanel } from './UI';
import Hero from './Hero';
import AboutSection from './About';
import { StickyFeatures, FeaturesCarousel } from './Features';
import PricingWithTransition from './Pricing';
import { CinematicVideoTransition, VideoModal } from './Media';
import FAQSection from './FAQ';
import JoinSection from './Join';

export function LandingPage() {
  const { t, locale } = useLanguage();
  const tl = t.landing;

  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState<any | null>(null);
  
  const [footerHeight, setFooterHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const footerRef = useRef<HTMLDivElement>(null);

  // Отслеживание мобильной версии
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Отслеживание достижения конца страницы для бесшовного перехода на ПК
  useEffect(() => {
    const handleScroll = () => {
      const scrolledToBottom =
        window.innerHeight + Math.round(window.scrollY) >=
        document.documentElement.scrollHeight - 10;
      setIsAtBottom(scrolledToBottom);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [footerHeight, isMobile]);

  useEffect(() => {
    const isAnyModalOpen = isTrailerOpen || isAppModalOpen || activeVideo !== null;

    window.dispatchEvent(
      new CustomEvent('lenis-lock', {
        detail: { isLocked: isAnyModalOpen },
      })
    );

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      window.dispatchEvent(
        new CustomEvent('lenis-lock', { detail: { isLocked: false } })
      );
      document.body.style.overflow = '';
    };
  }, [isTrailerOpen, isAppModalOpen, activeVideo]);

  useEffect(() => {
    const updateHeight = () => {
      if (footerRef.current) setFooterHeight(footerRef.current.offsetHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    
    let observer: ResizeObserver;
    if (footerRef.current) {
      observer = new ResizeObserver(updateHeight);
      observer.observe(footerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateHeight);
      if (observer) observer.disconnect();
    };
  }, []);

  // Флаг: нужно ли скрыть тень, скругления и рамку (на мобилке всегда, на ПК - в самом низу)
  const hideTransition = isMobile || isAtBottom;

  return (
    <main className="relative overflow-x-clip bg-[#111111] text-[#ffffff] selection:bg-[#667fff] selection:text-white pt-[115px] sm:pt-[88px] min-h-screen">
      {/* Глобальные стили для страницы */}
      <style jsx global>{`
        @font-face { font-family: 'MinecraftCustom'; src: url('https://webstatic.royolands.com/upload/contentweb/minecraft.ttf') format('truetype'); font-weight: normal; font-style: normal; font-display: swap; }
        :root { --bg: #111111; --fg: #ffffff; --fg-muted: #aaaaaa; --card-bg: #161616; --card-border: rgba(255, 255, 255, 0.05); }
        .font-mc { font-family: 'MinecraftCustom', sans-serif !important; font-weight: normal !important; letter-spacing: normal !important; }
        * { -webkit-tap-highlight-color: transparent; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .will-change-transform { will-change: transform; }
        .will-change-opacity-transform { will-change: opacity, transform; }
        @keyframes marquee-left { 0% { transform: translate3d(0, 0, 0); } 100% { transform: translate3d(-50%, 0, 0); } }
        @keyframes marquee-right { 0% { transform: translate3d(-50%, 0, 0); } 100% { transform: translate3d(0, 0, 0); } }
        .animate-marquee-left { animation: marquee-left 90s linear infinite; }
        .animate-marquee-right { animation: marquee-right 90s linear infinite; }
        .pause-on-hover:hover .animate-marquee-left, .pause-on-hover:hover .animate-marquee-right { animation-play-state: paused !important; }
        .is-paused .animate-marquee-left, .is-paused .animate-marquee-right { animation-play-state: paused !important; }
        
        /* Стили для UI кнопок */
        .vw-97legi { position: relative; pointer-events: auto; display: flex; align-items: center; justify-content: center; gap: 1rem; width: max-content; min-width: 24rem; max-width: 90vw; height: 5.5rem; padding: 0px 1.5rem; clip-path: polygon(0rem 2.25rem, 0.25rem 2.25rem, 0.25rem 1.5rem, 0.5rem 1.5rem, 0.5rem 1rem, 0.75rem 1rem, 0.75rem 0.75rem, 1rem 0.75rem, 1rem 0.5rem, 1.5rem 0.5rem, 1.5rem 0.25rem, 2.25rem 0.25rem, 2.25rem 0rem, calc(100% - 2.25rem) 0rem, calc(100% - 2.25rem) 0.25rem, calc(100% - 1.5rem) 0.25rem, calc(100% - 1.5rem) 0.5rem, calc(100% - 1rem) 0.5rem, calc(100% - 1rem) 0.75rem, calc(100% - 0.75rem) 0.75rem, calc(100% - 0.75rem) 1rem, calc(100% - 0.5rem) 1rem, calc(100% - 0.5rem) 1.5rem, calc(100% - 0.25rem) 1.5rem, calc(100% - 0.25rem) 2.25rem, calc(100% + 0rem) 2.25rem, calc(100% + 0rem) calc(100% - 2.25rem), calc(100% - 0.25rem) calc(100% - 2.25rem), calc(100% - 0.25rem) calc(100% - 1.5rem), calc(100% - 0.5rem) calc(100% - 1.5rem), calc(100% - 0.5rem) calc(100% - 1rem), calc(100% - 0.75rem) calc(100% - 1rem), calc(100% - 0.75rem) calc(100% - 0.75rem), calc(100% - 1rem) calc(100% - 0.75rem), calc(100% - 1rem) calc(100% - 0.5rem), calc(100% - 1.5rem) calc(100% - 0.5rem), calc(100% - 1.5rem) calc(100% - 0.25rem), calc(100% - 2.25rem) calc(100% - 0.25rem), calc(100% - 2.25rem) calc(100% + 0rem), 2.25rem calc(100% + 0rem), 2.25rem calc(100% - 0.25rem), 1.5rem calc(100% - 0.25rem), 1.5rem calc(100% - 0.5rem), 1rem calc(100% - 0.5rem), 1rem calc(100% - 0.75rem), 0.75rem calc(100% - 0.75rem), 0.75rem calc(100% - 1rem), 0.5rem calc(100% - 1rem), 0.5rem calc(100% - 1.5rem), 0.25rem calc(100% - 1.5rem), 0.25rem calc(100% - 2.25rem), 0rem calc(100% - 2.25rem)); background-color: rgba(255, 255, 255, 0.08); backdrop-filter: blur(7.1875rem) saturate(0.7); background-image: linear-gradient(rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08)), linear-gradient(rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08)); background-size: calc(100% - 4.5rem) 1px; background-position: center top, center bottom; background-repeat: no-repeat; }
        button.vw-k5dpkc { outline: none; border-width: medium; border-style: none; border-color: currentcolor; border-image: none; background: none; font: inherit; }
        a.vw-1aj4o6k { text-decoration: none; }
        .vw-k5dpkc { --fill: #fff; display: inline-flex; align-items: stretch; flex: 1 0 0px; min-width: 0px; height: 3.5rem; padding: 0px; color: #000 !important; font-family: inherit; font-weight: 600; line-height: 1; text-align: center; cursor: pointer; transition: opacity 0.2s, transform 0.2s; border-radius: 2.1875rem; overflow: hidden; }
        .vw-tegvy6 { display: flex; flex: 1 0 0px; min-width: 0px; align-items: center; justify-content: center; gap: 0.5rem; padding: 0px 1.5rem; white-space: nowrap; background: var(--fill); color: #000 !important; transition: background 0.2s; border-top: none; border-bottom: none; cursor: pointer; }
        .vw-k5dpkc:hover .vw-tegvy6 { background: #e0e0e0; }
        .vw-1aj4o6k { --fill: #fff; display: inline-flex; align-items: stretch; flex: 0 0 auto; min-width: 0px; height: 3.5rem; padding: 0px; color: #fff !important; font-family: inherit; font-weight: 600; line-height: 1; text-align: center; cursor: pointer; transition: opacity 0.2s, transform 0.2s; border-radius: 2.1875rem; overflow: hidden; }
        .vw-3u6176 { display: flex; flex: 1 0 0px; min-width: 0px; align-items: center; justify-content: center; gap: 0.5rem; padding: 0px 1.75rem; white-space: nowrap; background: transparent; border-top: 0.15rem solid var(--fill); border-bottom: 0.15rem solid var(--fill); color: #fff !important; transition: background 0.2s; cursor: pointer;}
        .vw-1aj4o6k:hover .vw-3u6176 { background: rgba(255, 255, 255, 0.15); }
      `}</style>

      <CompanyHeader />
      
      <div 
        className={`relative z-10 w-full bg-[#111111] pt-[4px] border-b transition-[border-radius,box-shadow,border-color] duration-300 ${
          hideTransition 
            ? 'rounded-b-none shadow-none border-transparent' 
            : 'rounded-b-[20px] md:rounded-b-[40px] shadow-[0_25px_50px_-12px_rgba(0,0,0,1)] border-white/5'
        }`} 
        style={{ marginBottom: isMobile ? 0 : footerHeight }}
      >
        <AmbientLight />

        <Header />

        <div className="w-full max-w-[120rem] mx-auto px-4 md:px-14 xl:px-[11.25rem] -mt-[4px]">
          <Hero onOpenTrailer={() => setIsTrailerOpen(true)} />
          <AboutSection />
        </div>

        <StickyFeatures />
        <CinematicVideoTransition activeVideo={activeVideo} setActiveVideo={setActiveVideo} />
        <FeaturesCarousel />
        <PricingWithTransition onOpenAppModal={() => setIsAppModalOpen(true)} />
        <FAQSection />
        <JoinSection />

        <div className="sticky bottom-6 md:bottom-10 w-full flex justify-center pointer-events-none z-[150]" style={{ transform: 'translateY(-30px)' }}>
          <StickyBottomPanel />
        </div>
      </div>

      <AnimatePresence>
          {isTrailerOpen && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 z-[200] flex items-center justify-center px-4 bg-black/95 touch-none pointer-events-auto cursor-pointer" onClick={() => setIsTrailerOpen(false)}>
                  <style dangerouslySetInnerHTML={{ __html: `html, body { overflow: hidden !important; overscroll-behavior: none !important; }`}} />
                  <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-5xl aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl border border-white/10 pointer-events-auto cursor-default">
                      <button onClick={() => setIsTrailerOpen(false)} className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors cursor-pointer"><X size={20} /></button>
                      
                      {locale === 'ru' ? (
                        <iframe 
                          src="https://vkvideo.ru/video_ext.php?oid=-208979302&id=456239028&hash=68269d2d40b8867d&hd=4&autoplay=1" 
                          allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock;" 
                          className="w-full h-full border-0"
                          allowFullScreen
                        />
                      ) : (
                        <iframe 
                          src="" 
                          allow="autoplay; encrypted-media; fullscreen" 
                          className="w-full h-full border-0" 
                          allowFullScreen
                        />
                      )}
                      
                  </motion.div>
              </motion.div>
          )}
      </AnimatePresence>

      <AnimatePresence>
        {activeVideo && (
          <VideoModal 
            video={activeVideo} 
            onClose={() => setActiveVideo(null)} 
            onNext={() => {
                const nextId = showcaseVideos.findIndex(v => v.id === activeVideo.id) + 1;
                const nextVideo = showcaseVideos[nextId >= showcaseVideos.length ? 0 : nextId];
                setActiveVideo({...nextVideo, sourceRow: activeVideo.sourceRow, sourceIdx: activeVideo.sourceIdx});
            }}
            onPrev={() => {
                const prevId = showcaseVideos.findIndex(v => v.id === activeVideo.id) - 1;
                const prevVideo = showcaseVideos[prevId < 0 ? showcaseVideos.length - 1 : prevId];
                setActiveVideo({...prevVideo, sourceRow: activeVideo.sourceRow, sourceIdx: activeVideo.sourceIdx});
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isAppModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 pointer-events-auto" onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
            <style dangerouslySetInnerHTML={{ __html: `html, body { overflow: hidden !important; overscroll-behavior: none !important; }`}} />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} onClick={() => setIsAppModalOpen(false)} className="absolute inset-0 bg-black/80 cursor-pointer" />
            <motion.div initial={{ opacity: 0, scale: 0.98, y: 15 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, y: 15 }} transition={{ type: "spring", stiffness: 400, damping: 30 }} className="relative w-full max-w-lg bg-[#0c0c0c] border border-white/10 rounded-[2rem] shadow-2xl overflow-hidden flex flex-col cursor-default">
                <div className="absolute top-4 right-4 z-10">
                    <button onClick={() => setIsAppModalOpen(false)} className="cursor-pointer p-3 bg-white/5 hover:bg-white/10 rounded-full transition-colors text-[#aaaaaa] hover:text-white outline-none"><X size={18} strokeWidth={2.5}/></button>
                </div>
                <div className="p-8 sm:p-10">
                    <div className="mb-10 mt-2">
                        <div className="w-10 h-1 bg-[#667fff] rounded-full mb-6" />
                        <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-2 uppercase">{tl.modal.title}</h2>
                        <p className="text-[#aaaaaa] font-medium leading-relaxed">{tl.modal.desc}</p>
                    </div>
                    <div className="space-y-4">
                        
                        {locale === 'ru' && (
                          <a href="https://t.me/RpLandsBot" target="_blank" className="flex items-center gap-5 p-5 w-full rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.08] hover:border-white/10 transition-all duration-300 cursor-pointer group outline-none">
                             <div className="w-12 h-12 rounded-full bg-[#2AABEE]/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                               <IconTelegram className="w-6 h-6 text-[#2AABEE] -mt-[1px] -ml-[1px]" />
                             </div>
                             <div className="text-left flex-1">
                               <h4 className="text-white font-bold text-lg">{tl.modal.tgTitle}</h4>
                               <p className="text-[#aaaaaa] text-sm mt-0.5">{tl.modal.tgDesc}</p>
                             </div>
                             <ChevronRight size={20} className="text-white/20 group-hover:text-white/60 transition-colors" />
                          </a>
                        )}
                        <Link href="/about/multiverse/application" className="flex items-center gap-5 p-5 w-full rounded-2xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.08] hover:border-white/10 transition-all duration-300 cursor-pointer group outline-none">
                           <div className="w-12 h-12 rounded-full bg-[#4CAF50]/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                             <FileText className="w-6 h-6 text-[#4CAF50]" />
                           </div>
                           <div className="text-left flex-1">
                             <h4 className="text-white font-bold text-lg">
                               {tl.modal.webTitle || (locale === 'ru' ? 'Анкета на сайте' : 'Website Application')}
                             </h4>
                             <p className="text-[#aaaaaa] text-sm mt-0.5">
                               {tl.modal.webDesc || (locale === 'ru' ? 'Заполнить заявку без сторонних мессенджеров' : 'Fill out the form on the website')}
                             </p>
                           </div>
                           <ChevronRight size={20} className="text-white/20 group-hover:text-white/60 transition-colors" />
                        </Link>

                    </div>
                </div>
                <div className="px-8 sm:px-10 py-6 bg-white/[0.02] border-t border-white/5">
                    <p className="text-sm font-medium text-[#aaaaaa]">{tl.modal.wait}<span className="text-white font-bold">{tl.modal.waitTime}</span></p>
                </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div 
        ref={footerRef} 
        className={`${isMobile ? 'relative' : 'fixed bottom-0 left-0'} w-full z-0 pointer-events-auto flex flex-col justify-end`}
      >
        <div className={`bg-[#111111] relative w-full border-t transition-colors duration-300 ${hideTransition ? 'border-transparent' : 'border-white/5'}`}>
          <Footer />
        </div>
      </div>
    </main>
  );
}